import Constants from "../../constants";
import { i18n } from "../locales/i18n";
import {User} from "./user";


export class Factoring {
    factoring = null;
    user = null;
    params = null;

    constructor (factoring = null, user = null, params = null) {
        this.factoring = factoring;
        this.user = user;
        this.params = params;
    }

    isSimpleModel () {
        return this.factoring.payer.cif === this.factoring.receiver.cif;
    }

    isFactorModel () {
        if (this.factoring.invoice_compensated) return true;
        return false;
    }

    isTriangleModel () {
        return !this.isSimpleModel() && !this.isFactorModel();
    }

    acceptedPayments () {
        return this.factoring.acceptedPaymentMethods;
    }

    iAmThePayer () {
        return this.user && this.user.cif === this.factoring.payer.cif;
    }

    iAmTheReceiver () {
        return this.user && this.user.cif === this.factoring.receiver.cif;
    }

    iAmTheMiddle () {
        return this.user && this.user.cif === this.factoring.middle.cif;
    }

    iHaveAccepted () {
        if (this.factoring.acceptedBy === 0 && this.iAmThePayer()) return true;
        if (this.factoring.acceptedBy < 2 && this.iAmTheMiddle()) return true;
        if (this.factoring.acceptedBy === 1 && this.iAmTheReceiver()) return true;
        if (this.factoring.acceptedBy === 3) return true;
        return false;
    }

    isFinished () {
        return this.factoring.status === Constants.FACTORING.STATUS_PAYED;
    }

    status () {
        return this.factoring.status;
    }

    getPendingText () {
        if (this.isSimpleModel()) {
            return i18n.t('Your partner') + " <b>" + this.factoring.middle.cif + "</b> " + i18n.t('is asking you to simplify the next invoices:');
        } else if (this.isFactorModel()) {
            if (this.iAmThePayer()) {
                return i18n.t('User') + " <b>" + this.factoring.receiver.cif + "</b> " + i18n.t('offers you to change the next invoice:');
            } else {
                return i18n.t('User') + " <b>" + this.factoring.middle.cif + "</b> " + i18n.t('offers to pay you the next invoice:');
            }
        } else {
            return i18n.t('Your partner') + " <b>" + this.factoring.middle.cif + "</b> " + i18n.t( 'is proposing this changes: ');//i18n.t( 'is going to pay this invoice to you by asking his client to do it: ');
        }
    }

    /**
     * Text functions
     */

    calculateRemainingTextSimple () {
        const rem = this.calculateRemaining();
        if (rem === 0) {
            return i18n.t('No more payments required!');
        }

        if (this.iAmTheMiddle()) {
            if (rem > 0) {
                return i18n.t('Remaining to pay:') + " <b>" + Math.abs(rem) + " €</b>";
            } else if (rem < 0) {
                return i18n.t('Remaining to pay:') + " <b>" + Math.abs(rem) + " €</b>";
            }
        } else if (!this.iAmTheMiddle()) {
            if (rem > 0) {
                return i18n.t('Remaining to receive:') + " <b>" + Math.abs(rem) + " €</b>";
            } else if (rem < 0) {
                return i18n.t('Remaining to receive:') + " <b>" + Math.abs(rem) + " €</b>";
            }
        }

        return "Error";
    }

    calculateRemainingTextFactor () {
        const rem = this.calculateRemaining();
        if (rem === 0) {
            return i18n.t('No more payments required!');
        }

        if (rem > 0) {
            return i18n.t('Remaining to pay:') + " <b>" + Math.abs(rem) + " €</b>";
        } else if (rem < 0) {
            return i18n.t('Remaining to receive:') + " <b>" + Math.abs(rem) + " €</b>";
        }

        return "Error";
    }

    calculateRemainingTextTriangle () {
        const rem = this.calculateRemaining();
        if (rem === 0) {
            return i18n.t('No more payments required!');
        }

        // 3 actors
        if (rem > 0) {
            return i18n.t('Remaining to receive:') + " <b>" + Math.abs(rem) + " €</b>";
        } else if (rem < 0) {
            return i18n.t('Remaining to pay:') + " <b>" + Math.abs(rem) + " €</b>";
        }

        return "Error";
    }

    calculateRemainingText () {
        if (this.isSimpleModel()) return this.calculateRemainingTextSimple();
        else if (this.isFactorModel()) return this.calculateRemainingTextFactor();
        else return this.calculateRemainingTextTriangle();
    }

    calculateProposalLine1 (logged) {
        // Only 2 actors
        if (this.isSimpleModel()) {
            const rem = this.calculateRemaining();
            if (rem > 0) {
                return i18n.t('You pay') + " <b>"  + rem + " €</b> " + i18n.t('to') + " <b>" + this.factoring.middle.cif + "</b>";
            } else if (rem < 0) {
                return "<b>"  + this.factoring.middle.cif + "</b> " + i18n.t('will pay you') + " <b>" + Math.abs(rem) + " €</b>";
            } else {
                return i18n.t('No more payments required!');
            }
        }

        // Factor
        if (this.isFactorModel()) {
            if (this.iAmTheReceiver()) {
                if (this.factoring.invoice_receiver) {
                    return i18n.t('You pay') + " <b>" + this.factoring.invoice_receiver.amount + " €</b> " + i18n.t('to') + " <b>" + this.factoring.middle.cif + "</b>";
                }
                return "";
            } else if (this.iAmThePayer()) {
                return "";
            } else {
                if (this.factoring.invoice_compensated) {
                    return i18n.t('You pay') + " <b>" + this.factoring.invoice_compensated.amount + " €</b> " + i18n.t('to') + " <b>" + this.factoring.receiver.cif + "</b>";
                }
                return "";
            }
        }

        // 3 actors
        const amount = this.calculatePayerAmount();
        let originalAmount = 0;
        if ( this.user && (this.factoring.invoice_receiver.payerCif === this.user.cif || this.factoring.invoice_receiver.receiverCif === this.user.cif)) {
            originalAmount = this.factoring.invoice_receiver.amount;
        } else {
            originalAmount = this.factoring.invoice_payer.amount;
        }

        if (amount === 0) {
            return i18n.t('No more payments required!');
        }
        if (this.iAmTheReceiver()) {
            let bonus = 0;
            let user;
            console.log(this.factoring.amount);
            console.log(this.factoring.compensation);
            console.log(originalAmount);
            logged ? user = "you " : user = this.factoring.receiver.cif;
            if (this.factoring.invoice_receiver.amount >= this.factoring.invoice_payer.amount)
                bonus = (this.factoring.amount + this.factoring.compensation) - originalAmount;
            else {
                bonus = this.factoring.compensation - originalAmount;
            }

            return "<b>"  + this.factoring.payer.cif + "</b> " + i18n.t('will pay to ') + "<b>" + user  + "</b> " + "<b style='color: #43E1B2'> "  + this.factoring.compensation +
                "€ </b> " + i18n.t('of the invoice.') + "<br/>" +
                i18n.t("So ") + "<b>" + user + "</b>" + i18n.t(" get a bonus of ") + "<b style='color: #43E1B2'>"  + bonus.toFixed(2) + "€ </b>";
        } else if (this.iAmThePayer()) {
            let user;
            logged ? user = "You " : user = this.factoring.payer.cif;
            const bonus = this.factoring.invoice_payer.amount - (this.factoring.compensation + this.factoring.amount);
            if (bonus < 0)
                return "<b>" + user + "</b> " + i18n.t(' will pay ') + " <b style='color: #43E1B2'>"  + this.factoring.compensation + " €</b> " + i18n.t('to') + " <b>" + this.factoring.receiver.cif + "</b>";
            else
                return "<b>" + user + "</b> " + i18n.t(' will pay ') + " <b style='color: #43E1B2'>"  + this.factoring.compensation + " €</b> " + i18n.t('to') + " <b>" + this.factoring.receiver.cif + "</b>"
                    + "<br/>" + "<b>" + this.factoring.payer.cif + "</b>" + i18n.t(' get a bonus of ') + "<b style='color: #43E1B2' >" +  bonus.toFixed(2) + "€. </b>";

        } else if (this.iAmTheMiddle()) {
            return "<b>"  + this.factoring.payer.cif + "</b> " + i18n.t('will pay') + " <b>" + this.factoring.receiver.cif + "</b> " + " <b>" + " " + amount + " €</b>";
        }
        return "";
    }

    calculateProposalLine2 (logged) {
        // With 2 actors, no second line
        if (this.isSimpleModel()) {
            return "";
        }

        // Factor
        if (this.isFactorModel()) {
            if (this.factoring.invoice_comission && this.factoring.invoice_comission.amount > 0) {
                if (this.iAmTheReceiver()) {
                    return i18n.t('You pay') + " <b>" + this.factoring.invoice_comission.amount + " €</b> " + i18n.t('to') + " <b>" + this.factoring.payer.cif + "</b>";
                } else if (this.iAmThePayer()) {
                    return i18n.t('You receive') + " <b>" + this.factoring.invoice_comission.amount + " €</b> " + i18n.t('from') + " <b>" + this.factoring.receiver.cif + "</b>";
                } else {
                    return i18n.t('You receive') + " <b>" + this.factoring.invoice_receiver.amount + " €</b> " + i18n.t('from') + " <b>" + this.factoring.receiver.cif + "</b>";
                }
            } else {
                return "";
            }
        }

        // 3 actors
        if (this.iAmTheReceiver()) {
            const midAmount = this.factoring.amount;
            let user;
            logged ? user = "you " : user = this.factoring.receiver.cif;
            if (midAmount) {
                if (this.factoring.invoice_payer.amount < this.factoring.invoice_receiver.amount) {
                    return "<b>" + this.factoring.middle.cif + "</b> " + i18n.t('will pay to ') + "<b>" + user + " </b>" + " <b style='color: #43E1B2'> " + midAmount + " €</b>";
                }
            }
        } else if (this.iAmThePayer()) {
            let user;
            logged ? user = "You " : user = this.factoring.payer.cif;
            if (this.factoring.invoice_receiver.amount < this.factoring.invoice_payer.amount) {
                const midAmount = this.factoring.amount;//this.calculateToMiddleAmount();
                if (midAmount) {
                    return "<b>" + user + " </b>" + i18n.t(' will pay ') + " <b style='color: #43E1B2'>"  + midAmount + " €</b> " + i18n.t('to') + " <b>" + this.factoring.middle.cif + "</b>";
                }
            } else {
                const bonus = this.factoring.invoice_payer.amount - this.factoring.compensation;
                return "<b>" + user + " </b>" + i18n.t(' get a bonus of ') + " <b style='color: #43E1B2'>"  + bonus.toFixed(2) + " €.</b> ";
            }

            const midAmount = this.factoring.amount;
            if (midAmount) {
                return "<b>" + user + " </b>" + i18n.t(' will pay ') + " <b style='color: #43E1B2'>"  + midAmount + " €</b> " + i18n.t('to') + " <b>" + this.factoring.middle.cif + "</b>";
            }
        } else if (this.iAmTheMiddle()) {
            const amount = this.calculateRemaining();
            if (amount === 0) {
                return i18n.t('No more payments required!');
            }
        }
        return "";
    }

    calculateProgressColor () {
        if ([Constants.FACTORING.STATUS_CREATED, Constants.FACTORING.STATUS_ACCEPTED].indexOf(this.factoring.status) !== -1) {
            return "cyan";
        } else if ([Constants.FACTORING.STATUS_PAYED].indexOf(this.factoring.status) !== -1) {
            return "light-green";
        }
        if ([Constants.FACTORING.STATUS_DISCARDED, Constants.FACTORING.STATUS_REJECTED].indexOf(this.factoring.status) !== -1) {
            return "#FF6961";
        }
    }

    getStatusText () {
        switch (this.factoring.status) {
        case Constants.FACTORING.STATUS_CREATED:
            return i18n.t('Waiting for your partner') + " <b>" + this.factoring.receiver.cif + "</b> " + i18n.t('to accept or deny this bypay.');
        case Constants.FACTORING.STATUS_REJECTED:
            return i18n.t('Your partner') + " <b>" + this.factoring.receiver.cif + "</b> " + i18n.t('has denied this bypay.');
        case Constants.FACTORING.STATUS_ACCEPTED:
            return i18n.t('Waiting for your partner') + " <b>" + this.factoring.payer.cif + "</b> " + i18n.t('to accept or deny this bypay.');
        case Constants.FACTORING.STATUS_UNPAYED:
            return i18n.t('Your partner') + " <b>" + this.factoring.payer.cif + "</b> " + i18n.t('has denied this bypay.');
        case Constants.FACTORING.STATUS_PAYED:
            return i18n.t('This bypay has been approved by') + " <b> " + i18n.t('all') + " </b> " + i18n.t('partners!');
        }
        return "";
    }

    getPersonForStatusText () {
        switch (this.factoring.status) {
        case Constants.FACTORING.STATUS_CREATED:
            return this.factoring.receiver.cif;
        case Constants.FACTORING.STATUS_REJECTED:
            return this.factoring.receiver.cif;
        case Constants.FACTORING.STATUS_ACCEPTED:
            return this.factoring.payer.cif;
        case Constants.FACTORING.STATUS_UNPAYED:
            return this.factoring.payer.cif;
        }
        return "";
    }

    /**
     * Amount functions
     */

    calculateRemaining () {
        if (this.factoring.invoice_receiver.amount > this.factoring.invoice_payer.amount) {
            return (this.factoring.invoice_receiver.amount - ((this.factoring.invoice_payer.amount * (1 + this.receiverComission())) / (1 - this.payerComission()))).toFixed(2);
        } else {
            return (this.factoring.invoice_payer.amount - ((this.factoring.invoice_receiver.amount * (1 + this.receiverComission())) / (1 - this.payerComission()))).toFixed(2);
        }
    }

    calculatePayerAmount () {
        if (this.factoring.invoice_modified) {
            return this.factoring.invoice_modified.amount * (1 + this.payerComission());
        }
        if (this.factoring.invoice_receiver.amount < this.factoring.invoice_payer.amount) {
            return this.factoring.invoice_receiver.amount * (1 + this.receiverComission());
        }
        return this.factoring.invoice_payer.amount * (1 + this.payerComission());
    }

    calculateToMiddleAmount () {
        if (this.factoring.invoice_comission) {
            return this.factoring.invoice_comission.amount * (1 - this.payerComission());
        }
        if (this.factoring.invoice_payer.amount > this.factoring.invoice_receiver.amount) {
            return (this.factoring.invoice_payer.amount - ((this.factoring.invoice_receiver.amount * (1 + this.receiverComission())) / (1 - this.payerComission()))).toFixed(2);
        }
        return 0;
    }

    payerComission () {
        if (this.params != null && this.params.payerComission) return this.params.payerComission;
        return Constants.payerComission;
    }

    receiverComission () {
        if (this.params != null && this.params.receiverComission) return this.params.receiverComission;
        return Constants.receiverComission;
    }

    calculateFromMiddleAmount () {
        if (this.factoring.invoice_receiver.amount > this.factoring.invoice_payer.amount) {
            return (this.factoring.invoice_receiver.amount - ((this.factoring.invoice_payer.amount * (1 + this.receiverComission())) / (1 - this.payerComission()))).toFixed(2);
        }
        return 0;
    }

    calculateProgressValue () {
        switch (this.factoring.status) {
        case Constants.FACTORING.STATUS_CREATED:
            return (this.isSimpleModel()) ? 50 : 33;
        case Constants.FACTORING.STATUS_ACCEPTED:
            return (this.isSimpleModel()) ? 50 : 66;
        case Constants.FACTORING.STATUS_PAYED:
            return 100;
        }

        return 0;
    }

    payerHadToPayReceiver () {
        return true;
    }
    payerHadToPayPlatform () {
        return false;
    }
    payerHadToPayMiddle () {
        return false;
    }
    middleHadToPayReceiver () {
        return false;
    }
    middleHadToPayPlatform () {
        return true;
    }
    payerToReceiverUnpayed () {
        return this.factoring.payerPaymentToReceiver === Constants.FACTORING.STATUS_UNPAYED;
    }
    payerToPlatformUnpayed () {
        return false;
    }
    middleToReceiverUnpayed () {
        return this.factoring.middlePaymentToReceiver === Constants.FACTORING.STATUS_UNPAYED;
    }
    middleToPlatformUnpayed () {
        return this.factoring.middlePaymentToPlatform === Constants.FACTORING.STATUS_UNPAYED;
    }
    payerToMiddleUnpayed () {
        return this.factoring.payerPaymentToMiddle === Constants.FACTORING.STATUS_UNPAYED;
    }

    receiverFromPayerPayed () {
        return this.factoring.payerPaymentToReceiver === Constants.FACTORING.STATUS_PAYED;
    }
    platformFromPayerPayed () {
        return false;
    }
    receiverFromMiddlePayed () {
        return this.factoring.middlePaymentToReceiver === Constants.FACTORING.STATUS_PAYED;
    }
    platformFromMiddlePayed () {
        return this.factoring.middlePaymentToPlatform === Constants.FACTORING.STATUS_PAYED;
    }
    middleFromPayerPayed () {
        return this.factoring.payerPaymentToMiddle === Constants.FACTORING.STATUS_PAYED;
    }

    receiverFromPayerAccepted () {
        return this.factoring.payerPaymentToReceiver === Constants.FACTORING.STATUS_ACCEPTED;
    }
    platformFromPayerAccepted () {
        return false;
    }
    receiverFromMiddleAccepted () {
        return this.factoring.middlePaymentToReceiver === Constants.FACTORING.STATUS_ACCEPTED;
    }
    platformFromMiddleAccepted () {
        return this.factoring.middlePaymentToPlatform === Constants.FACTORING.STATUS_ACCEPTED;
    }
    middleFromPayerAccepted () {
        return this.factoring.payerPaymentToMiddle === Constants.FACTORING.STATUS_ACCEPTED;
    }

    paymentInformation () {
        const ret = {line1: '', line2: ''};

        const userPayerModel = new User(this.factoring.payer);
        const userMiddleModel = new User(this.factoring.middle);
        const userReceiverModel = new User(this.factoring.receiver);

        if (this.iAmThePayer()) { // A
            if (this.payerHadToPayReceiver()) { // A have to pay to C
                if (this.payerToReceiverUnpayed() && userReceiverModel.stripePublickey !== '') { // A unpayed and C have stripe account
                    ret.line1 = i18n.t('payment.confirmPaymenStripeto', {user: userReceiverModel.getName()});
                } else if (this.payerToReceiverUnpayed()) { // A unpayed
                    ret.line1 = i18n.t('payment.confirmPaymentto', {user: userReceiverModel.getName()});
                } else if (this.receiverFromPayerPayed()) { // A payed, waiting C to accept
                    ret.line1 = i18n.t('payment.waitingConfirmationto', {user: userReceiverModel.getName()});
                } else if (this.receiverFromPayerAccepted()) { // C accept the payment
                    ret.line1 = i18n.t('payment.acceptPaymentFrom', {user: userReceiverModel.getName()});
                }
            }
            if (this.payerHadToPayMiddle()) { // A have to pay to B
                if (this.payerToMiddleUnpayed() && userMiddleModel.stripePublickey !== '') { // A unpayed and b have stripe account
                    ret.line2 = i18n.t('payment.confirmPaymenStripeto', {user: userMiddleModel.getName()});
                } else if (this.payerToMiddleUnpayed()) { // A unpayed
                    ret.line2 = i18n.t('payment.confirmPaymentto', {user: userMiddleModel.getName()});
                }
                if (this.middleFromPayerPayed()) { // A payed, waiting B to accept
                    ret.line2 = i18n.t('payment.waitingConfirmationto', {user: userMiddleModel.getName()});
                }
                if (this.middleFromPayerAccepted()) { // B accept the payment
                    ret.line2 = i18n.t('payment.acceptPaymentFrom', {user: userMiddleModel.getName()});
                }
            }
        } else if (this.iAmTheMiddle()) { // B
            if (this.middleHadToPayReceiver()) { // B have to pay to C
                if (this.middleToReceiverUnpayed() && userReceiverModel.stripePublickey !== '') { // B unpayed and receiver have stripe account
                    ret.line1 = i18n.t('payment.confirmPaymenStripeto', {user: userReceiverModel.getName()});
                } else if  (this.middleToReceiverUnpayed()) { // B unpayed
                    ret.line1 = i18n.t('payment.confirmPaymentto', {user: userReceiverModel.getName()});
                } else if (this.receiverFromMiddlePayed()) { // B payed, waiting C to accept
                    ret.line1 = i18n.t('payment.waitingConfirmationto', {user: userReceiverModel.getName()});
                } else if (this.receiverFromMiddleAccepted()) { // C accept the payment
                    ret.line1 = i18n.t('payment.acceptPaymentFrom', {user: userReceiverModel.getName()});
                }
            }
            if (this.middleHadToPayPlatform()) {
                if (this.middleToPlatformUnpayed()) { // B unpayed and receiver have stripe account
                    ret.line1 = i18n.t('payment.confirmPaymento', {user: 'Platform'});
                } else if (this.platformFromMiddlePayed()) { // B unpayed and receiver have stripe account
                    ret.line1 = i18n.t('payment.acceptPaymentFrom', {user: 'Platform'});
                } else if (this.platformFromMiddleAccepted()) { // B unpayed and receiver have stripe account
                    ret.line1 = i18n.t('payment.acceptPaymentFrom', {user: 'Platform'});
                }
            }
            if (this.payerHadToPayMiddle()) { // B have to receive from A
                if (this.payerToMiddleUnpayed()) { // A unpayed
                    ret.line2 = i18n.t('payment.waitingPaymentFrom', {user: userPayerModel.getName()});
                }
                if (this.middleFromPayerPayed()) { // A payed, waiting B to accept
                    ret.line2 = i18n.t('payment.confirmPaymentFrom', {user: userPayerModel.getName()});
                }
                if (this.middleFromPayerAccepted()) { // B accept the payment
                    ret.line2 = i18n.t('payment.acceptPayment', {user: userMiddleModel.getName()});
                }
            }
        } else if (this.iAmTheReceiver()) { // C
            if (this.payerHadToPayReceiver()) { // A have to pay to C
                if (this.payerToReceiverUnpayed()) { // A unpayed
                    ret.line1 = i18n.t('payment.waitingPaymentFrom', {user: userPayerModel.getName()});
                } else if (this.receiverFromPayerPayed()) { // A payed, waiting C to accept
                    ret.line1 = i18n.t('payment.confirmPaymentFrom', {user: userPayerModel.getName()});
                } else if (this.receiverFromPayerAccepted()) { // C accept the payment
                    ret.line1 = i18n.t('payment.receivedFrom', {user: userPayerModel.getName()});
                }
            }
            if (this.middleHadToPayReceiver()) { // B have to pay to C
                if (this.middleToReceiverUnpayed()) { // B unpayed
                    ret.line2 = i18n.t('payment.waitingPaymentFrom', {user: userMiddleModel.getName()});
                } else if (this.receiverFromMiddlePayed()) { // B payed, waiting C to accept
                    ret.line2 = i18n.t('payment.confirmPaymentFrom', {user: userMiddleModel.getName()});
                } else if (this.receiverFromMiddleAccepted()) { // C accept the payment
                    ret.line2 = i18n.t('payment.receivedFrom', {user: userMiddleModel.getName()});
                }
            }
        }
        return ret;
    }
}
